import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Alert,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Checkbox,
  FormControlLabel as MUIFormControlLabel,
  Autocomplete
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  Home as HomeIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarIcon,
  Favorite as FavoriteIcon,
  Group as GroupIcon,
  ChildCare as ChildCareIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  Restaurant as RestaurantIcon,
  DriveEta as TransportIcon,
  ShoppingBag as ShoppingIcon,
  Receipt as BillsIcon,
  LocalGroceryStore as FoodIcon,
  Build as UtilitiesIcon,
  LocalHospital as HealthIcon,
  SportsEsports as EntertainmentIcon,
  FlightTakeoff as TravelIcon,
  Category as DefaultIcon
} from '@mui/icons-material';
import { incomeService, budgetService } from '../../../services/apiService';
import transactionService from '../../transactions/services/transactionService';
import { useAuthContext } from '../../auth/context/AuthContext';
import toast from 'react-hot-toast';

// Reduced to 6 steps
const STEPS = [
  { label: 'Income', icon: AttachMoneyIcon },
  { label: 'Lifestyle', icon: PeopleIcon },
  { label: 'Spending & Expenses', icon: TrendingUpIcon },
  { label: 'Categories', icon: AccountBalanceIcon },
  { label: 'Budget Rule', icon: HomeIcon },
  { label: 'Review', icon: CheckCircleIcon }
];

// US States for location autocomplete
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

// Popular US Cities for autocomplete (simplified - can be expanded)
const POPULAR_CITIES = [
  'New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego',
  'Dallas', 'San Jose', 'Austin', 'Jacksonville', 'Fort Worth', 'Columbus', 'Charlotte', 'San Francisco',
  'Indianapolis', 'Seattle', 'Denver', 'Washington', 'Boston', 'El Paso', 'Nashville', 'Detroit',
  'Oklahoma City', 'Portland', 'Las Vegas', 'Memphis', 'Louisville', 'Baltimore', 'Milwaukee', 'Albuquerque',
  'Tucson', 'Fresno', 'Sacramento', 'Kansas City', 'Mesa', 'Atlanta', 'Omaha', 'Colorado Springs',
  'Raleigh', 'Virginia Beach', 'Miami', 'Oakland', 'Minneapolis', 'Tulsa', 'Cleveland', 'Wichita', 'Arlington'
];

const BUDGET_RULES = {
  '50-30-20': {
    name: '50/30/20 Rule',
    description: '50% Needs, 30% Wants, 20% Savings',
    needs: 0.50,
    wants: 0.30,
    savings: 0.20
  },
  'zero-based': {
    name: 'Zero-Based Budget',
    description: 'Every dollar assigned to a category',
    needs: 0.40,
    wants: 0.30,
    savings: 0.30
  },
  'envelope': {
    name: 'Envelope Method',
    description: 'Fixed amounts per category',
    needs: 0.45,
    wants: 0.25,
    savings: 0.30
  },
  'custom': {
    name: 'Custom',
    description: 'Set your own percentages',
    needs: 0.40,
    wants: 0.30,
    savings: 0.30
  }
};

// Cute loading messages for spending analysis
const LOADING_MESSAGES = [
  "🔍 Counting your coffee runs...",
  "💰 Calculating your shopping sprees...",
  "🍕 Analyzing your food adventures...",
  "🚗 Tracking your travel expenses...",
  "🎬 Reviewing your entertainment choices...",
  "💳 Organizing your spending patterns...",
  "📊 Crunching the numbers...",
  "✨ Finding your spending trends..."
];

const GuidedBudgetModal = ({ open, onClose, onSuccess }) => {
  const { user } = useAuthContext();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  
  // Step 1: Income Analysis
  const [incomes, setIncomes] = useState([]);
  const [selectedIncomes, setSelectedIncomes] = useState(new Set());
  const [totalMonthlyIncome, setTotalMonthlyIncome] = useState(0);
  
  // Step 2: Lifestyle Info
  const [lifestyleData, setLifestyleData] = useState({
    age: '',
    maritalStatus: '',
    householdSize: '',
    children: '',
    employmentStatus: '',
    housingType: '',
    location: '',
    city: '',
    state: '',
    partnerAge: '',
    partnerEmploymentStatus: ''
  });
  
  // Validation errors for lifestyle step
  const [lifestyleErrors, setLifestyleErrors] = useState({});
  
  // Step 3: Spending & Expenses Analysis (combined)
  const [spendingData, setSpendingData] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [existingExpenses, setExistingExpenses] = useState([]);
  const [findings, setFindings] = useState([]);
  
  // Step 4: Category Suggestions
  const [suggestedCategories, setSuggestedCategories] = useState([]);
  
  // Step 5: Budget Rule
  const [selectedRule, setSelectedRule] = useState('50-30-20');
  
  // Step 6: Amount Recommendations & Review (combined)
  const [recommendedAmounts, setRecommendedAmounts] = useState({});

  // Fetch income data on mount
  useEffect(() => {
    if (open && activeStep === 0) {
      fetchIncomeData();
    }
  }, [open, activeStep]);

  // Auto-trigger spending analysis when step 2 is reached
  useEffect(() => {
    if (open && activeStep === 2 && !spendingData && !analyzing) {
      analyzeSpending();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeStep]);

  // Auto-trigger category suggestions when step 3 is reached
  useEffect(() => {
    if (open && activeStep === 3 && suggestedCategories.length === 0 && spendingData) {
      generateCategorySuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeStep, spendingData]);

  // Rotate loading messages
  useEffect(() => {
    if (analyzing) {
      let messageIndex = 0;
      const interval = setInterval(() => {
        setLoadingMessage(LOADING_MESSAGES[messageIndex]);
        messageIndex = (messageIndex + 1) % LOADING_MESSAGES.length;
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [analyzing]);

  // Calculate total income from selected incomes
  const calculateTotalIncome = (incomesArray, selectedSet) => {
    let total = 0;
    incomesArray.forEach((income, idx) => {
      if (selectedSet.has(idx)) {
        const amount = income.netAmount || income.amount || 0;
        const frequency = income.frequency || 'monthly';
        
        if (frequency === 'monthly') {
          total += amount;
        } else if (frequency === 'weekly') {
          total += amount * 4.33; // Average weeks per month
        } else if (frequency === 'bi-weekly' || frequency === 'biweekly') {
          total += amount * 2.17; // Bi-weekly to monthly
        } else if (frequency === 'yearly') {
          total += amount / 12;
        } else if (frequency === 'semi-monthly' || frequency === 'semimonthly') {
          total += amount * 2;
        }
      }
    });
    setTotalMonthlyIncome(total);
  };

  const handleIncomeToggle = (index) => {
    const newSelected = new Set(selectedIncomes);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedIncomes(newSelected);
    calculateTotalIncome(incomes, newSelected);
  };

  const fetchIncomeData = async () => {
    try {
      setLoading(true);
      const hasPartner = !!(user?.partner || user?.partnerId);
      const response = await incomeService.getAllIncomes({ includePartner: hasPartner });
      
      const incomesArray = response?.data || response || [];
      setIncomes(incomesArray);
      
      // Default all incomes to selected
      const allIncomeIds = new Set(incomesArray.map((income, idx) => idx));
      setSelectedIncomes(allIncomeIds);
      
      // Calculate total monthly income from selected incomes
      calculateTotalIncome(incomesArray, allIncomeIds);
      
      setLoading(false);
      setCompletedSteps(prev => new Set([...prev, 0]));
    } catch (error) {
      console.error('Error fetching income:', error);
      toast.error('Failed to load income data');
      setLoading(false);
    }
  };

  const analyzeSpending = async () => {
    try {
      setAnalyzing(true);
      setFindings([]);
      setLoadingMessage(LOADING_MESSAGES[0]);
      
      // Simulate progressive findings (15% faster - 3974ms instead of 4675ms)
      let findingCounter = 0;
      const addFinding = (message, delay = 3974) => {
        return new Promise(resolve => {
          setTimeout(() => {
            findingCounter++;
            setFindings(prev => [...prev, { id: Date.now() + findingCounter, message }]);
            resolve();
          }, delay);
        });
      };
      
      // Get last 6 months of transactions
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 6);
      
      await addFinding('📅 Analyzing last 6 months of transactions...', 723); // 15% faster from 850ms
      
      const response = await transactionService.getAllTransactions({
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        limit: 1000
      });
      
      const transactions = response?.data?.transactions || [];
      await addFinding(`✅ Found ${transactions.length} transactions`, 3974);
      
      const expenseTransactions = transactions.filter(
        tx => (tx.amount && tx.amount < 0) || tx.type === 'expense'
      );
      await addFinding(`💰 Identified ${expenseTransactions.length} expense transactions`, 3974);
      
      // Group by category - ensure category is always a string
      const categorySpending = {};
      expenseTransactions.forEach(tx => {
        // Handle case where category might be an object
        let category = tx.category;
        if (typeof category === 'object' && category !== null) {
          category = category.name || category.label || 'Other';
        }
        category = category || 'Other';
        category = String(category); // Ensure it's a string
        
        const amount = Math.abs(tx.amount || 0);
        categorySpending[category] = (categorySpending[category] || 0) + amount;
      });
      
      const categoryCount = Object.keys(categorySpending).length;
      await addFinding(`📊 Categorized spending into ${categoryCount} categories`, 3974);
      
      // Calculate average monthly spending per category
      const monthsAnalyzed = 6;
      const monthlyAverages = {};
      Object.keys(categorySpending).forEach(category => {
        monthlyAverages[category] = categorySpending[category] / monthsAnalyzed;
      });
      
      const topCategory = Object.entries(monthlyAverages)
        .sort((a, b) => b[1] - a[1])[0];
      
      if (topCategory) {
        await addFinding(`🏆 Top spending category: ${topCategory[0]} ($${topCategory[1].toFixed(2)}/month)`, 3974);
      }
      
      const totalMonthly = Object.values(monthlyAverages).reduce((a, b) => a + b, 0);
      await addFinding(`💵 Average monthly spending: $${totalMonthly.toFixed(2)}`, 3974);
      
      setSpendingData({
        totalSpent: Object.values(categorySpending).reduce((a, b) => a + b, 0),
        monthlyAverage: totalMonthly,
        categoryAverages: monthlyAverages,
        transactionCount: expenseTransactions.length
      });
      
      setAnalyzing(false);
      setCompletedSteps(prev => new Set([...prev, 2]));
    } catch (error) {
      console.error('Error analyzing spending:', error);
      toast.error('Failed to analyze spending history');
      setAnalyzing(false);
    }
  };

  const generateCategorySuggestions = () => {
    const categories = [];
    
    // Add categories from spending analysis
    if (spendingData?.categoryAverages) {
      Object.keys(spendingData.categoryAverages).forEach(category => {
        if (spendingData.categoryAverages[category] > 0) {
          categories.push({
            name: category,
            suggestedAmount: spendingData.categoryAverages[category],
            source: 'spending-history'
          });
        }
      });
    }
    
    // Add lifestyle-based suggestions
    if (lifestyleData.housingType) {
      categories.push({
        name: 'Housing',
        suggestedAmount: totalMonthlyIncome * 0.30,
        source: 'lifestyle'
      });
    }
    
    if (lifestyleData.children && parseInt(lifestyleData.children) > 0) {
      categories.push({
        name: 'Childcare',
        suggestedAmount: totalMonthlyIncome * 0.10,
        source: 'lifestyle'
      });
    }
    
    setSuggestedCategories(categories);
    setCompletedSteps(prev => new Set([...prev, 3]));
  };

  const calculateRecommendations = () => {
    const rule = BUDGET_RULES[selectedRule];
    const recommendations = {};
    
    // Apply rule percentages
    const needsBudget = totalMonthlyIncome * rule.needs;
    const wantsBudget = totalMonthlyIncome * rule.wants;
    const savingsBudget = totalMonthlyIncome * rule.savings;
    
    // Distribute based on spending history and suggestions
    suggestedCategories.forEach(cat => {
      const categoryName = cat.name;
      const suggestedAmount = cat.suggestedAmount || 0;
      
      // Determine if it's a need or want
      const needsCategories = ['Housing', 'Utilities', 'Groceries', 'Transportation', 'Healthcare', 'Insurance', 'Childcare'];
      const isNeed = needsCategories.some(nc => categoryName.toLowerCase().includes(nc.toLowerCase()));
      
      if (isNeed) {
        recommendations[categoryName] = Math.min(suggestedAmount, needsBudget * 0.3);
      } else {
        recommendations[categoryName] = Math.min(suggestedAmount, wantsBudget * 0.2);
      }
    });
    
    setRecommendedAmounts(recommendations);
    setCompletedSteps(prev => new Set([...prev, 5]));
  };

  const handleStepClick = (stepIndex) => {
    // Allow clicking on completed steps to go back
    if (completedSteps.has(stepIndex) || stepIndex < activeStep) {
      setActiveStep(stepIndex);
    }
  };

  // Validate lifestyle step
  const validateLifestyleStep = () => {
    const errors = {};
    const required = ['age', 'maritalStatus', 'householdSize', 'children', 'employmentStatus', 'housingType'];
    
    required.forEach(field => {
      if (!lifestyleData[field]) {
        errors[field] = 'This field is required';
      }
    });
    
    // If married or domestic partner, require partner fields
    if (lifestyleData.maritalStatus === 'married' || lifestyleData.maritalStatus === 'domestic-partner') {
      if (!lifestyleData.partnerAge) {
        errors.partnerAge = 'Partner age range is required';
      }
      if (!lifestyleData.partnerEmploymentStatus) {
        errors.partnerEmploymentStatus = 'Partner employment status is required';
      }
    }
    
    setLifestyleErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    // Validate lifestyle step before proceeding
    if (activeStep === 1) {
      if (!validateLifestyleStep()) {
        toast.error('Please fill in all required fields');
        return;
      }
    }
    
    // Mark current step as completed
    setCompletedSteps(prev => new Set([...prev, activeStep]));
    
    // Category suggestions now auto-run when step 3 is reached
    if (activeStep === 4) {
      // Calculate recommendations
      calculateRecommendations();
    }
    
    if (activeStep < STEPS.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleCreateBudget = async () => {
    try {
      setLoading(true);
      
      // Calculate period dates (current month)
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      // Build categories array from recommendations
      const budgetCategories = Object.entries(recommendedAmounts).map(([name, amount]) => ({
        name,
        plannedAmount: amount,
        actualAmount: 0,
        rollover: false,
        frequency: 'monthly'
      }));
      
      // Build income array from SELECTED incomes only
      const budgetIncome = incomes
        .filter((income, idx) => selectedIncomes.has(idx))
        .map(income => {
        const amount = income.netAmount || income.amount || 0;
        const frequency = income.frequency || 'monthly';
        
        // Convert to monthly amount
        let monthlyAmount = amount;
        if (frequency === 'weekly') {
          monthlyAmount = amount * 4.33;
        } else if (frequency === 'bi-weekly' || frequency === 'biweekly') {
          monthlyAmount = amount * 2.17;
        } else if (frequency === 'yearly') {
          monthlyAmount = amount / 12;
        } else if (frequency === 'semi-monthly' || frequency === 'semimonthly') {
          monthlyAmount = amount * 2;
        }
        
        return {
          sourceId: income._id,
          name: income.name || 'Income',
          plannedAmount: monthlyAmount,
          netAmount: monthlyAmount,
          actualAmount: 0,
          type: 'individual',
          periodicity: 'monthly'
        };
      });
      
      // Calculate totals
      const totalPlannedIncome = budgetIncome.reduce((sum, inc) => sum + (inc.plannedAmount || 0), 0);
      const totalPlannedExpenses = budgetCategories.reduce((sum, cat) => sum + (cat.plannedAmount || 0), 0);
      const totalNetIncome = totalPlannedIncome - totalPlannedExpenses;
      
      // Create budget name based on rule and date
      const monthName = now.toLocaleString('default', { month: 'long' });
      const year = now.getFullYear();
      const budgetName = `${BUDGET_RULES[selectedRule].name} - ${monthName} ${year}`;
      
      // Create budget object
      const budgetData = {
        name: budgetName,
        description: `Guided budget created using ${BUDGET_RULES[selectedRule].name}. Based on ${lifestyleData.maritalStatus || 'your'} lifestyle and spending history.`,
        period: {
          type: 'monthly',
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        },
        categories: budgetCategories,
        income: budgetIncome,
        totalPlannedIncome,
        totalPlannedExpenses,
        totalNetIncome,
        isActive: true,
        isPrimary: false
      };
      
      console.log('Creating budget with data:', budgetData);
      
      const createdBudget = await budgetService.createBudget(budgetData);
      
      toast.success('Budget created successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error creating budget:', error);
      toast.error('Failed to create budget: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Animated Progress Bar Component with Smooth Color Transitions
  const AnimatedProgressBar = () => {
    // Calculate progress percentage (0-100)
    const progress = ((activeStep + 1) / STEPS.length) * 100;
    
    // Simplified cohesive color scheme: Lemonade pink → Purple → Blue → Teal
    // Clean 4-color gradient that flows smoothly
    const baseColors = {
      pink: '#f093fb',        // Lemonade pink
      purple: '#667eea',      // Purple
      blue: '#4facfe',        // Blue
      teal: '#30cfd0'         // Teal
    };
    
    // Create a simple, clean gradient: pink → purple → blue → teal
    const cohesiveGradient = `linear-gradient(90deg, 
      ${baseColors.pink} 0%, 
      ${baseColors.purple} 33%, 
      ${baseColors.blue} 66%, 
      ${baseColors.teal} 100%
    )`;
    
    // Use the same cohesive gradient for all steps to maintain color consistency
    const currentGradient = cohesiveGradient;
    const nextGradient = cohesiveGradient;
    
    return (
      <Box sx={{ mb: 4, mt: 2, width: '100%' }}>
        {/* Step Labels */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mb: 1.5,
          position: 'relative',
          zIndex: 2
        }}>
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isCompleted = completedSteps.has(index);
            const isActive = activeStep === index;
            const isPast = index < activeStep;
            
            return (
              <Box
                key={index}
                onClick={() => (isCompleted || isPast) && handleStepClick(index)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: (isCompleted || isPast) ? 'pointer' : 'default',
                  flex: 1,
                  transition: 'all 0.3s ease',
                  transform: isActive ? 'scale(1.1)' : 'scale(1)',
                  zIndex: isActive ? 10 : 1
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isCompleted || isPast
                      ? cohesiveGradient
                      : isActive
                        ? cohesiveGradient
                        : 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
                    color: isCompleted || isActive || isPast ? 'white' : '#9ca3af',
                    boxShadow: isActive
                      ? `0 4px 20px rgba(102, 126, 234, 0.4)`
                      : isCompleted
                        ? `0 2px 10px rgba(102, 126, 234, 0.2)`
                        : 'none',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    '&::after': isActive ? {
                      content: '""',
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      background: cohesiveGradient,
                      opacity: 0.6,
                      animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { transform: 'scale(1)', opacity: 0.6 },
                        '50%': { transform: 'scale(1.3)', opacity: 0 }
                      }
                    } : {}
                  }}
                >
                  {isCompleted ? (
                    <CheckCircleIcon sx={{ fontSize: 24 }} />
                  ) : (
                    <Icon sx={{ fontSize: 20 }} />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    fontSize: '0.7rem',
                    fontWeight: isActive ? 'bold' : isCompleted ? '600' : '400',
                    color: isActive || isCompleted || isPast ? '#667eea' : '#9ca3af',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {step.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
        
        {/* Animated Progress Bar - 2x taller */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 24, // Doubled from 12px to 24px
            borderRadius: 12, // Doubled from 6px to 12px
            background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
            overflow: 'hidden',
            boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
        >
          {/* Animated Fill with Cohesive Gradient */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: `${progress}%`,
              background: cohesiveGradient,
              borderRadius: 12, // Doubled from 6px to 12px
              transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: `0 0 20px rgba(102, 126, 234, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
                animation: 'shimmer 2s infinite',
                '@keyframes shimmer': {
                  '0%': { transform: 'translateX(-100%)' },
                  '100%': { transform: 'translateX(100%)' }
                }
              }
            }}
          />
          
          {/* Progress Indicator Dot - Larger for thicker bar */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: `${progress}%`,
              transform: 'translate(-50%, -50%)',
              width: 28, // Slightly larger for the thicker bar
              height: 28,
              borderRadius: '50%',
              background: cohesiveGradient,
              border: '4px solid white', // Thicker border for visibility
              boxShadow: `0 0 0 4px rgba(102, 126, 234, 0.2), 0 4px 12px rgba(0, 0, 0, 0.3)`,
              transition: 'left 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 10,
              animation: 'pulseDot 2s ease-in-out infinite',
              '@keyframes pulseDot': {
                '0%, 100%': { transform: 'translate(-50%, -50%) scale(1)' },
                '50%': { transform: 'translate(-50%, -50%) scale(1.1)' }
              }
            }}
          />
        </Box>
        
        {/* Progress Percentage */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: '#667eea',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            {Math.round(progress)}% Complete
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#667eea' }}>
              💰 Your Monthly Income
            </Typography>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading your income sources...
                </Typography>
              </Box>
            ) : (
              <>
                <Card sx={{ 
                  mt: 3, 
                  mb: 3, 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
                }}>
                  <CardContent>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                      ${totalMonthlyIncome.toFixed(2)}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      Total Monthly Net Income
                    </Typography>
                  </CardContent>
                </Card>
                {incomes.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                      Income Sources ({incomes.length}):
                    </Typography>
                    <Grid container spacing={2}>
                      {incomes.map((income, idx) => {
                        const amount = income.netAmount || income.amount || 0;
                        const frequency = income.frequency || 'monthly';
                        const isSelected = selectedIncomes.has(idx);
                        
                        // Calculate monthly amount for display
                        let monthlyAmount = amount;
                        if (frequency === 'weekly') {
                          monthlyAmount = amount * 4.33;
                        } else if (frequency === 'bi-weekly' || frequency === 'biweekly') {
                          monthlyAmount = amount * 2.17;
                        } else if (frequency === 'yearly') {
                          monthlyAmount = amount / 12;
                        } else if (frequency === 'semi-monthly' || frequency === 'semimonthly') {
                          monthlyAmount = amount * 2;
                        }
                        
                        return (
                          <Grid item xs={12} sm={6} key={idx}>
                            <Card sx={{ 
                              bgcolor: isSelected ? '#f5f5f5' : '#fafafa',
                              border: isSelected ? '2px solid #667eea' : '2px solid #e0e0e0',
                              opacity: isSelected ? 1 : 0.6,
                              transition: 'all 0.3s ease',
                              '&:hover': { 
                                borderColor: '#667eea',
                                opacity: 1,
                                transform: 'translateY(-2px)',
                                boxShadow: 4
                              }
                            }}>
                              <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                      {income.name || 'Income'}
                                    </Typography>
                                    <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                                      ${amount.toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                      ${monthlyAmount.toFixed(2)}/month
                                    </Typography>
                                    <Chip 
                                      label={frequency} 
                                      size="small" 
                                      color="primary"
                                      sx={{ mt: 0.5 }}
                                    />
                                  </Box>
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleIncomeToggle(idx)}
                                    sx={{
                                      color: '#667eea',
                                      '&.Mui-checked': {
                                        color: '#667eea',
                                      },
                                    }}
                                  />
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
              </>
            )}
          </Box>
        );
      
      case 1:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#667eea', mb: 1 }}>
              Lifestyle Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Select the options that best describe your situation
            </Typography>
            
            <Grid container spacing={4}>
              {/* Marital Status - FIRST FIELD */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <FavoriteIcon sx={{ color: '#667eea', mr: 1, fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                    Marital Status <Typography component="span" sx={{ color: '#ef4444' }}>*</Typography>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {[
                    { value: 'single', label: 'Single', icon: PersonIcon },
                    { value: 'married', label: 'Married', icon: FavoriteIcon },
                    { value: 'domestic-partner', label: 'Domestic Partner', icon: PeopleIcon },
                    { value: 'divorced', label: 'Divorced', icon: PersonIcon },
                    { value: 'widowed', label: 'Widowed', icon: PersonIcon }
                  ].map((option) => {
                    const isSelected = lifestyleData.maritalStatus === option.value;
                    const IconComponent = option.icon;
                    return (
                      <Button
                        key={option.value}
                        variant={isSelected ? 'contained' : 'outlined'}
                        onClick={() => {
                          setLifestyleData({
                            ...lifestyleData, 
                            maritalStatus: option.value,
                            partnerAge: (option.value === 'married' || option.value === 'domestic-partner') ? lifestyleData.partnerAge : '',
                            partnerEmploymentStatus: (option.value === 'married' || option.value === 'domestic-partner') ? lifestyleData.partnerEmploymentStatus : ''
                          });
                          setLifestyleErrors({...lifestyleErrors, maritalStatus: ''});
                        }}
                        startIcon={<IconComponent sx={{ fontSize: 16 }} />}
                        sx={{
                          minWidth: 140,
                          borderColor: lifestyleErrors.maritalStatus ? '#ef4444' : '#d1d5db',
                          borderWidth: 2,
                          color: isSelected ? 'white' : '#374151',
                          bgcolor: isSelected ? '#667eea' : 'transparent',
                          fontWeight: isSelected ? 600 : 400,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          transform: 'scale(1)',
                          boxShadow: isSelected ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                          '&:hover': {
                            borderColor: '#667eea',
                            bgcolor: isSelected 
                              ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                              : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                            background: isSelected 
                              ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                              : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                            transform: 'scale(1.05)',
                            boxShadow: isSelected 
                              ? '0 6px 20px rgba(102, 126, 234, 0.5)' 
                              : '0 4px 12px rgba(102, 126, 234, 0.3)',
                            borderWidth: 2,
                            borderColor: '#667eea'
                          },
                          '&:active': {
                            transform: 'scale(0.98)'
                          }
                        }}
                      >
                        {option.label}
                      </Button>
                    );
                  })}
                </Box>
                {lifestyleErrors.maritalStatus && (
                  <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5, display: 'block' }}>
                    {lifestyleErrors.maritalStatus}
                  </Typography>
                )}
              </Grid>
              
              {/* Your Information & Partner Information - Show when Married or Domestic Partner */}
              {(lifestyleData.maritalStatus === 'married' || lifestyleData.maritalStatus === 'domestic-partner') && (
                <>
                  {/* Your Information Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#667eea', mt: 2, mb: 2 }}>
                      Your Information
                    </Typography>
                  </Grid>
                  
                  {/* Your Age Range */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CalendarIcon sx={{ color: '#667eea', mr: 1, fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                        Your Age Range <Typography component="span" sx={{ color: '#ef4444' }}>*</Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {['18-25', '26-35', '36-45', '46-55', '56-65', '65+'].map((range) => {
                        const isSelected = lifestyleData.age === range;
                        return (
                          <Button
                            key={range}
                            variant={isSelected ? 'contained' : 'outlined'}
                            onClick={() => {
                              setLifestyleData({...lifestyleData, age: range});
                              setLifestyleErrors({...lifestyleErrors, age: ''});
                            }}
                            startIcon={isSelected ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : null}
                            sx={{
                              minWidth: 100,
                              borderColor: lifestyleErrors.age ? '#ef4444' : '#d1d5db',
                              borderWidth: 2,
                              color: isSelected ? 'white' : '#374151',
                              bgcolor: isSelected ? '#667eea' : 'transparent',
                              fontWeight: isSelected ? 600 : 400,
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              transform: 'scale(1)',
                              boxShadow: isSelected ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                              '&:hover': {
                                borderColor: '#667eea',
                                bgcolor: isSelected 
                                  ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                background: isSelected 
                                  ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                transform: 'scale(1.05)',
                                boxShadow: isSelected 
                                  ? '0 6px 20px rgba(102, 126, 234, 0.5)' 
                                  : '0 4px 12px rgba(102, 126, 234, 0.3)',
                                borderWidth: 2,
                                borderColor: '#667eea'
                              },
                              '&:active': {
                                transform: 'scale(0.98)'
                              }
                            }}
                          >
                            {range}
                          </Button>
                        );
                      })}
                    </Box>
                    {lifestyleErrors.age && (
                      <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5, display: 'block' }}>
                        {lifestyleErrors.age}
                      </Typography>
                    )}
                  </Grid>
                  
                  {/* Your Employment Status */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <WorkIcon sx={{ color: '#667eea', mr: 1, fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                        Your Employment <Typography component="span" sx={{ color: '#ef4444' }}>*</Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {[
                        { value: 'full-time', label: 'Full-Time', icon: WorkIcon },
                        { value: 'part-time', label: 'Part-Time', icon: WorkIcon },
                        { value: 'self-employed', label: 'Self-Employed', icon: BusinessIcon },
                        { value: 'unemployed', label: 'Unemployed', icon: PersonIcon },
                        { value: 'retired', label: 'Retired', icon: PersonIcon },
                        { value: 'student', label: 'Student', icon: SchoolIcon }
                      ].map((option) => {
                        const isSelected = lifestyleData.employmentStatus === option.value;
                        const IconComponent = option.icon;
                        return (
                          <Button
                            key={option.value}
                            variant={isSelected ? 'contained' : 'outlined'}
                            onClick={() => {
                              setLifestyleData({...lifestyleData, employmentStatus: option.value});
                              setLifestyleErrors({...lifestyleErrors, employmentStatus: ''});
                            }}
                            startIcon={<IconComponent sx={{ fontSize: 16 }} />}
                            sx={{
                              minWidth: 120,
                              borderColor: lifestyleErrors.employmentStatus ? '#ef4444' : '#d1d5db',
                              borderWidth: 2,
                              color: isSelected ? 'white' : '#374151',
                              bgcolor: isSelected ? '#667eea' : 'transparent',
                              fontWeight: isSelected ? 600 : 400,
                              fontSize: '0.875rem',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              transform: 'scale(1)',
                              boxShadow: isSelected ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                              '&:hover': {
                                borderColor: '#667eea',
                                bgcolor: isSelected 
                                  ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                background: isSelected 
                                  ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                transform: 'scale(1.05)',
                                boxShadow: isSelected 
                                  ? '0 6px 20px rgba(102, 126, 234, 0.5)' 
                                  : '0 4px 12px rgba(102, 126, 234, 0.3)',
                                borderWidth: 2,
                                borderColor: '#667eea'
                              },
                              '&:active': {
                                transform: 'scale(0.98)'
                              }
                            }}
                          >
                            {option.label}
                          </Button>
                        );
                      })}
                    </Box>
                    {lifestyleErrors.employmentStatus && (
                      <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5, display: 'block' }}>
                        {lifestyleErrors.employmentStatus}
                      </Typography>
                    )}
                  </Grid>
                  
                  {/* Partner Information Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#667eea', mt: 2, mb: 2 }}>
                      Partner Information
                    </Typography>
                  </Grid>
                  
                  {/* Partner Age Range */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CalendarIcon sx={{ color: '#667eea', mr: 1, fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                        Partner Age Range <Typography component="span" sx={{ color: '#ef4444' }}>*</Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {['18-25', '26-35', '36-45', '46-55', '56-65', '65+'].map((range) => {
                        const isSelected = lifestyleData.partnerAge === range;
                        return (
                          <Button
                            key={range}
                            variant={isSelected ? 'contained' : 'outlined'}
                            onClick={() => {
                              setLifestyleData({...lifestyleData, partnerAge: range});
                              setLifestyleErrors({...lifestyleErrors, partnerAge: ''});
                            }}
                            startIcon={isSelected ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : null}
                            sx={{
                              minWidth: 100,
                              borderColor: lifestyleErrors.partnerAge ? '#ef4444' : '#d1d5db',
                              borderWidth: 2,
                              color: isSelected ? 'white' : '#374151',
                              bgcolor: isSelected ? '#667eea' : 'transparent',
                              fontWeight: isSelected ? 600 : 400,
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              transform: 'scale(1)',
                              boxShadow: isSelected ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                              '&:hover': {
                                borderColor: '#667eea',
                                bgcolor: isSelected 
                                  ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                background: isSelected 
                                  ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                transform: 'scale(1.05)',
                                boxShadow: isSelected 
                                  ? '0 6px 20px rgba(102, 126, 234, 0.5)' 
                                  : '0 4px 12px rgba(102, 126, 234, 0.3)',
                                borderWidth: 2,
                                borderColor: '#667eea'
                              },
                              '&:active': {
                                transform: 'scale(0.98)'
                              }
                            }}
                          >
                            {range}
                          </Button>
                        );
                      })}
                    </Box>
                    {lifestyleErrors.partnerAge && (
                      <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5, display: 'block' }}>
                        {lifestyleErrors.partnerAge}
                      </Typography>
                    )}
                  </Grid>
                  
                  {/* Partner Employment Status */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <WorkIcon sx={{ color: '#667eea', mr: 1, fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                        Partner Employment <Typography component="span" sx={{ color: '#ef4444' }}>*</Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {[
                        { value: 'full-time', label: 'Full-Time', icon: WorkIcon },
                        { value: 'part-time', label: 'Part-Time', icon: WorkIcon },
                        { value: 'self-employed', label: 'Self-Employed', icon: BusinessIcon },
                        { value: 'unemployed', label: 'Unemployed', icon: PersonIcon },
                        { value: 'retired', label: 'Retired', icon: PersonIcon },
                        { value: 'student', label: 'Student', icon: SchoolIcon }
                      ].map((option) => {
                        const isSelected = lifestyleData.partnerEmploymentStatus === option.value;
                        const IconComponent = option.icon;
                        return (
                          <Button
                            key={option.value}
                            variant={isSelected ? 'contained' : 'outlined'}
                            onClick={() => {
                              setLifestyleData({...lifestyleData, partnerEmploymentStatus: option.value});
                              setLifestyleErrors({...lifestyleErrors, partnerEmploymentStatus: ''});
                            }}
                            startIcon={<IconComponent sx={{ fontSize: 16 }} />}
                            sx={{
                              minWidth: 120,
                              borderColor: lifestyleErrors.partnerEmploymentStatus ? '#ef4444' : '#d1d5db',
                              borderWidth: 2,
                              color: isSelected ? 'white' : '#374151',
                              bgcolor: isSelected ? '#667eea' : 'transparent',
                              fontWeight: isSelected ? 600 : 400,
                              fontSize: '0.875rem',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              transform: 'scale(1)',
                              boxShadow: isSelected ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                              '&:hover': {
                                borderColor: '#667eea',
                                bgcolor: isSelected 
                                  ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                background: isSelected 
                                  ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                transform: 'scale(1.05)',
                                boxShadow: isSelected 
                                  ? '0 6px 20px rgba(102, 126, 234, 0.5)' 
                                  : '0 4px 12px rgba(102, 126, 234, 0.3)',
                                borderWidth: 2,
                                borderColor: '#667eea'
                              },
                              '&:active': {
                                transform: 'scale(0.98)'
                              }
                            }}
                          >
                            {option.label}
                          </Button>
                        );
                      })}
                    </Box>
                    {lifestyleErrors.partnerEmploymentStatus && (
                      <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5, display: 'block' }}>
                        {lifestyleErrors.partnerEmploymentStatus}
                      </Typography>
                    )}
                  </Grid>
                </>
              )}
              
              {/* Age Range & Employment Status - Show when NOT Married or Domestic Partner */}
              {(lifestyleData.maritalStatus !== 'married' && lifestyleData.maritalStatus !== 'domestic-partner') && (
                <>
                  {/* Age Range */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CalendarIcon sx={{ color: '#667eea', mr: 1, fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                        Age Range <Typography component="span" sx={{ color: '#ef4444' }}>*</Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {['18-25', '26-35', '36-45', '46-55', '56-65', '65+'].map((range) => {
                        const isSelected = lifestyleData.age === range;
                        return (
                          <Button
                            key={range}
                            variant={isSelected ? 'contained' : 'outlined'}
                            onClick={() => {
                              setLifestyleData({...lifestyleData, age: range});
                              setLifestyleErrors({...lifestyleErrors, age: ''});
                            }}
                            startIcon={isSelected ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : null}
                            sx={{
                              minWidth: 100,
                              borderColor: lifestyleErrors.age ? '#ef4444' : '#d1d5db',
                              borderWidth: 2,
                              color: isSelected ? 'white' : '#374151',
                              bgcolor: isSelected ? '#667eea' : 'transparent',
                              fontWeight: isSelected ? 600 : 400,
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              transform: 'scale(1)',
                              boxShadow: isSelected ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                              '&:hover': {
                                borderColor: '#667eea',
                                bgcolor: isSelected 
                                  ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                background: isSelected 
                                  ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                transform: 'scale(1.05)',
                                boxShadow: isSelected 
                                  ? '0 6px 20px rgba(102, 126, 234, 0.5)' 
                                  : '0 4px 12px rgba(102, 126, 234, 0.3)',
                                borderWidth: 2,
                                borderColor: '#667eea'
                              },
                              '&:active': {
                                transform: 'scale(0.98)'
                              }
                            }}
                          >
                            {range}
                          </Button>
                        );
                      })}
                    </Box>
                    {lifestyleErrors.age && (
                      <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5, display: 'block' }}>
                        {lifestyleErrors.age}
                      </Typography>
                    )}
                  </Grid>
                  
                  {/* Employment Status */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <WorkIcon sx={{ color: '#667eea', mr: 1, fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                        Employment Status <Typography component="span" sx={{ color: '#ef4444' }}>*</Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {[
                        { value: 'full-time', label: 'Full-Time', icon: WorkIcon },
                        { value: 'part-time', label: 'Part-Time', icon: WorkIcon },
                        { value: 'self-employed', label: 'Self-Employed', icon: BusinessIcon },
                        { value: 'unemployed', label: 'Unemployed', icon: PersonIcon },
                        { value: 'retired', label: 'Retired', icon: PersonIcon },
                        { value: 'student', label: 'Student', icon: SchoolIcon }
                      ].map((option) => {
                        const isSelected = lifestyleData.employmentStatus === option.value;
                        const IconComponent = option.icon;
                        return (
                          <Button
                            key={option.value}
                            variant={isSelected ? 'contained' : 'outlined'}
                            onClick={() => {
                              setLifestyleData({...lifestyleData, employmentStatus: option.value});
                              setLifestyleErrors({...lifestyleErrors, employmentStatus: ''});
                            }}
                            startIcon={<IconComponent sx={{ fontSize: 16 }} />}
                            sx={{
                              minWidth: 120,
                              borderColor: lifestyleErrors.employmentStatus ? '#ef4444' : '#d1d5db',
                              borderWidth: 2,
                              color: isSelected ? 'white' : '#374151',
                              bgcolor: isSelected ? '#667eea' : 'transparent',
                              fontWeight: isSelected ? 600 : 400,
                              fontSize: '0.875rem',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              transform: 'scale(1)',
                              boxShadow: isSelected ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                              '&:hover': {
                                borderColor: '#667eea',
                                bgcolor: isSelected 
                                  ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                background: isSelected 
                                  ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                transform: 'scale(1.05)',
                                boxShadow: isSelected 
                                  ? '0 6px 20px rgba(102, 126, 234, 0.5)' 
                                  : '0 4px 12px rgba(102, 126, 234, 0.3)',
                                borderWidth: 2,
                                borderColor: '#667eea'
                              },
                              '&:active': {
                                transform: 'scale(0.98)'
                              }
                            }}
                          >
                            {option.label}
                          </Button>
                        );
                      })}
                    </Box>
                    {lifestyleErrors.employmentStatus && (
                      <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5, display: 'block' }}>
                        {lifestyleErrors.employmentStatus}
                      </Typography>
                    )}
                  </Grid>
                </>
              )}
              
              {/* Household Size */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <GroupIcon sx={{ color: '#667eea', mr: 1, fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                    Household Size <Typography component="span" sx={{ color: '#ef4444' }}>*</Typography>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {['1', '2', '3', '4', '5+'].map((size) => {
                    const isSelected = lifestyleData.householdSize === size;
                    return (
                      <Button
                        key={size}
                        variant={isSelected ? 'contained' : 'outlined'}
                        onClick={() => {
                          setLifestyleData({...lifestyleData, householdSize: size});
                          setLifestyleErrors({...lifestyleErrors, householdSize: ''});
                        }}
                        startIcon={isSelected ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : null}
                        sx={{
                          minWidth: 60,
                          borderColor: lifestyleErrors.householdSize ? '#ef4444' : '#d1d5db',
                          borderWidth: 2,
                          color: isSelected ? 'white' : '#374151',
                          bgcolor: isSelected ? '#667eea' : 'transparent',
                          fontWeight: isSelected ? 600 : 400,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          transform: 'scale(1)',
                          boxShadow: isSelected ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                          '&:hover': {
                            borderColor: '#667eea',
                            bgcolor: isSelected ? '#5568d3' : '#f3f4f6',
                            transform: 'scale(1.1)',
                            boxShadow: isSelected 
                              ? '0 6px 16px rgba(102, 126, 234, 0.4)' 
                              : '0 4px 12px rgba(102, 126, 234, 0.2)',
                            borderWidth: 2
                          },
                          '&:active': {
                            transform: 'scale(0.95)'
                          }
                        }}
                      >
                        {size}
                      </Button>
                    );
                  })}
                </Box>
                {lifestyleErrors.householdSize && (
                  <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5, display: 'block' }}>
                    {lifestyleErrors.householdSize}
                  </Typography>
                )}
              </Grid>
              
              {/* Number of Children */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ChildCareIcon sx={{ color: '#667eea', mr: 1, fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                    Children <Typography component="span" sx={{ color: '#ef4444' }}>*</Typography>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {['0', '1', '2', '3', '4+', 'Expecting'].map((count) => {
                    const isSelected = lifestyleData.children === count;
                    return (
                      <Button
                        key={count}
                        variant={isSelected ? 'contained' : 'outlined'}
                        onClick={() => {
                          setLifestyleData({...lifestyleData, children: count});
                          setLifestyleErrors({...lifestyleErrors, children: ''});
                        }}
                        startIcon={isSelected ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : null}
                        sx={{
                          minWidth: 60,
                          borderColor: lifestyleErrors.children ? '#ef4444' : '#d1d5db',
                          borderWidth: 2,
                          color: isSelected ? 'white' : '#374151',
                          bgcolor: isSelected ? '#667eea' : 'transparent',
                          fontWeight: isSelected ? 600 : 400,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          transform: 'scale(1)',
                          boxShadow: isSelected ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                          '&:hover': {
                            borderColor: '#667eea',
                            bgcolor: isSelected ? '#5568d3' : '#f3f4f6',
                            transform: 'scale(1.1)',
                            boxShadow: isSelected 
                              ? '0 6px 16px rgba(102, 126, 234, 0.4)' 
                              : '0 4px 12px rgba(102, 126, 234, 0.2)',
                            borderWidth: 2
                          },
                          '&:active': {
                            transform: 'scale(0.95)'
                          }
                        }}
                      >
                        {count}
                      </Button>
                    );
                  })}
                </Box>
                {lifestyleErrors.children && (
                  <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5, display: 'block' }}>
                    {lifestyleErrors.children}
                  </Typography>
                )}
              </Grid>
              
              {/* Housing Type */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <HomeIcon sx={{ color: '#667eea', mr: 1, fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                    Housing Type <Typography component="span" sx={{ color: '#ef4444' }}>*</Typography>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {[
                    { value: 'own', label: 'Own', icon: HomeIcon },
                    { value: 'rent', label: 'Rent', icon: HomeIcon },
                    { value: 'live-with-family', label: 'With Family', icon: GroupIcon },
                    { value: 'other', label: 'Other', icon: HomeIcon }
                  ].map((option) => {
                    const isSelected = lifestyleData.housingType === option.value;
                    const IconComponent = option.icon;
                    return (
                      <Button
                        key={option.value}
                        variant={isSelected ? 'contained' : 'outlined'}
                        onClick={() => {
                          setLifestyleData({...lifestyleData, housingType: option.value});
                          setLifestyleErrors({...lifestyleErrors, housingType: ''});
                        }}
                        startIcon={<IconComponent sx={{ fontSize: 16 }} />}
                        sx={{
                          minWidth: 110,
                          borderColor: lifestyleErrors.housingType ? '#ef4444' : '#d1d5db',
                          borderWidth: 2,
                          color: isSelected ? 'white' : '#374151',
                          bgcolor: isSelected ? '#667eea' : 'transparent',
                          fontWeight: isSelected ? 600 : 400,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          transform: 'scale(1)',
                          boxShadow: isSelected ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                          '&:hover': {
                            borderColor: '#667eea',
                            bgcolor: isSelected 
                              ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                              : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                            background: isSelected 
                              ? 'linear-gradient(135deg, #5568d3 0%, #764ba2 100%)'
                              : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                            transform: 'scale(1.05)',
                            boxShadow: isSelected 
                              ? '0 6px 20px rgba(102, 126, 234, 0.5)' 
                              : '0 4px 12px rgba(102, 126, 234, 0.3)',
                            borderWidth: 2,
                            borderColor: '#667eea'
                          },
                          '&:active': {
                            transform: 'scale(0.98)'
                          }
                        }}
                      >
                        {option.label}
                      </Button>
                    );
                  })}
                </Box>
                {lifestyleErrors.housingType && (
                  <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5, display: 'block' }}>
                    {lifestyleErrors.housingType}
                  </Typography>
                )}
              </Grid>
              
              {/* Location - Optional with Autocomplete */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationIcon sx={{ color: '#667eea', mr: 1, fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                    Location <Typography component="span" sx={{ color: '#9ca3af', fontWeight: 400, fontSize: '0.875rem' }}>(Optional)</Typography>
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      freeSolo
                      options={POPULAR_CITIES}
                      value={lifestyleData.city || null}
                      onChange={(event, newValue) => {
                        setLifestyleData({...lifestyleData, city: newValue || ''});
                      }}
                      onInputChange={(event, newInputValue) => {
                        setLifestyleData({...lifestyleData, city: newInputValue});
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="City"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: <LocationIcon sx={{ color: '#9ca3af', mr: 1, fontSize: 18 }} />
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              transition: 'all 0.3s ease',
                              '& fieldset': {
                                borderColor: '#d1d5db',
                                borderWidth: 2
                              },
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                                borderWidth: 2
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#667eea',
                                borderWidth: 2
                              }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={US_STATES}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                      value={US_STATES.find(s => s.value === lifestyleData.state) || null}
                      onChange={(event, newValue) => {
                        setLifestyleData({...lifestyleData, state: newValue?.value || ''});
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="State"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              transition: 'all 0.3s ease',
                              '& fieldset': {
                                borderColor: '#d1d5db',
                                borderWidth: 2
                              },
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                                borderWidth: 2
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#667eea',
                                borderWidth: 2
                              }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        );
      
      case 2:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#667eea' }}>
              📊 Spending & Expenses Analysis
            </Typography>
            {analyzing ? (
              <Box>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={60} sx={{ color: '#667eea', mb: 3 }} />
                  <Typography variant="h6" sx={{ mb: 2, color: '#667eea', fontWeight: 'bold' }}>
                    {loadingMessage}
                  </Typography>
                  <LinearProgress sx={{ mt: 2, height: 8, borderRadius: 4 }} />
                </Box>
                
                {/* Real-time Findings Bubbles */}
                {findings.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, color: '#6b7280', fontWeight: 600 }}>
                      Discoveries:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {findings.map((finding) => (
                        <Card
                          key={finding.id}
                          sx={{
                            p: 2,
                            background: 'linear-gradient(135deg, #e0e7ff 0%, #f3e8ff 100%)',
                            border: '1px solid #c7d2fe',
                            borderRadius: 2,
                            animation: 'fadeIn 0.5s ease-in',
                            '@keyframes fadeIn': {
                              from: { opacity: 0, transform: 'translateY(-10px)' },
                              to: { opacity: 1, transform: 'translateY(0)' }
                            }
                          }}
                        >
                          <Typography variant="body2" sx={{ color: '#4b5563', fontWeight: 500 }}>
                            {finding.message}
                          </Typography>
                        </Card>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            ) : spendingData ? (
              <Box>
                <Card sx={{ 
                  mt: 2, 
                  mb: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
                }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Spending Summary</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      ${spendingData.monthlyAverage.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Average Monthly Spending
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 1 }}>
                      Based on {spendingData.transactionCount} transactions over 6 months
                    </Typography>
                  </CardContent>
                </Card>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                  Top Spending Categories:
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(spendingData.categoryAverages)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 6)
                    .map(([category, amount]) => {
                      // Ensure category is always a string
                      const categoryName = typeof category === 'string' ? category : (category?.name || category?.label || 'Other');
                      return (
                        <Grid item xs={12} sm={6} md={4} key={categoryName}>
                          <Card sx={{ bgcolor: '#f5f5f5', border: '2px solid #e0e0e0' }}>
                            <CardContent>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {categoryName}
                              </Typography>
                              <Typography variant="h6" color="primary">
                                ${typeof amount === 'number' ? amount.toFixed(2) : '0.00'}/month
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                </Grid>
                
                {/* Keep findings visible after analysis completes - moved below categories */}
                {findings.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, color: '#6b7280', fontWeight: 600 }}>
                      Analysis Discoveries:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {findings.map((finding) => (
                        <Card
                          key={finding.id}
                          sx={{
                            p: 2,
                            background: 'linear-gradient(135deg, #e0e7ff 0%, #f3e8ff 100%)',
                            border: '1px solid #c7d2fe',
                            borderRadius: 2,
                            animation: 'fadeIn 0.5s ease-in',
                            '@keyframes fadeIn': {
                              from: { opacity: 0, transform: 'translateY(-10px)' },
                              to: { opacity: 1, transform: 'translateY(0)' }
                            }
                          }}
                        >
                          <Typography variant="body2" sx={{ color: '#4b5563', fontWeight: 500 }}>
                            {finding.message}
                          </Typography>
                        </Card>
                      ))}
                    </Box>
                  </Box>
                )}
                
                <Alert severity="info" sx={{ mt: 3 }}>
                  💡 We'll integrate your existing recurring expenses into the budget recommendations
                </Alert>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                Click "Next" to analyze your spending history and expenses
              </Alert>
            )}
          </Box>
        );
      
      case 3:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#667eea' }}>
              📋 Suggested Budget Categories
            </Typography>
            {suggestedCategories.length > 0 ? (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {suggestedCategories.map((cat, idx) => {
                  // Category icon mapping - moved outside function for better performance
                  const getCategoryIcon = (categoryName) => {
                    const iconMap = {
                      'Dining': RestaurantIcon,
                      'Restaurants': RestaurantIcon,
                      'Food': RestaurantIcon,
                      'Transportation': TransportIcon,
                      'Shopping': ShoppingIcon,
                      'Bills': BillsIcon,
                      'Housing': HomeIcon,
                      'Utilities': UtilitiesIcon,
                      'Healthcare': HealthIcon,
                      'Health': HealthIcon,
                      'Entertainment': EntertainmentIcon,
                      'Travel': TravelIcon,
                      'Childcare': ChildCareIcon,
                      'Child Care': ChildCareIcon,
                      'Income': AttachMoneyIcon,
                      'Other': DefaultIcon
                    };
                    
                    // Try exact match first
                    if (iconMap[categoryName]) {
                      return iconMap[categoryName];
                    }
                    
                    // Try case-insensitive match
                    const lowerName = categoryName.toLowerCase();
                    for (const [key, Icon] of Object.entries(iconMap)) {
                      if (key.toLowerCase() === lowerName) {
                        return Icon;
                      }
                    }
                    
                    return DefaultIcon;
                  };
                  
                  const CategoryIcon = getCategoryIcon(cat.name);
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <Card sx={{ 
                        bgcolor: '#f5f5f5',
                        border: '2px solid #e0e0e0',
                        '&:hover': { borderColor: '#667eea', boxShadow: 4 }
                      }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              bgcolor: '#e0e7ff',
                              color: '#667eea'
                            }}>
                              <CategoryIcon sx={{ fontSize: 24 }} />
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flex: 1 }}>
                              {cat.name}
                            </Typography>
                          </Box>
                          <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                            ${cat.suggestedAmount.toFixed(2)}/month
                          </Typography>
                          <Chip
                            label={cat.source === 'spending-history' ? '📊 Based on spending' : '🎨 Based on lifestyle'}
                            size="small"
                            sx={{ mt: 1.5 }}
                            color={cat.source === 'spending-history' ? 'primary' : 'secondary'}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Alert severity="info">
                Click "Next" to generate category suggestions
              </Alert>
            )}
          </Box>
        );
      
      case 4:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#667eea' }}>
              🎯 Choose Your Budget Rule
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedRule}
                onChange={(e) => setSelectedRule(e.target.value)}
              >
                {Object.entries(BUDGET_RULES).map(([key, rule]) => (
                  <Card 
                    key={key} 
                    sx={{ 
                      mb: 2, 
                      cursor: 'pointer',
                      border: selectedRule === key ? '3px solid #667eea' : '2px solid #e0e0e0',
                      transition: 'all 0.3s ease',
                      '&:hover': { 
                        borderColor: '#667eea',
                        boxShadow: 4,
                        transform: 'translateY(-2px)'
                      }
                    }}
                    onClick={() => setSelectedRule(key)}
                  >
                    <CardContent>
                      <FormControlLabel
                        value={key}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {rule.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {rule.description}
                            </Typography>
                            <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                              <Chip label={`Needs: ${(rule.needs * 100).toFixed(0)}%`} size="small" color="primary" />
                              <Chip label={`Wants: ${(rule.wants * 100).toFixed(0)}%`} size="small" color="secondary" />
                              <Chip label={`Savings: ${(rule.savings * 100).toFixed(0)}%`} size="small" color="success" />
                            </Box>
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        );
      
      case 5:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#667eea' }}>
              ✨ Review & Create Your Budget
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
                }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>Monthly Income</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      ${totalMonthlyIncome.toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(245, 87, 108, 0.3)'
                }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>Budget Rule</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {BUDGET_RULES[selectedRule].name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Recommended Budget Amounts
                    </Typography>
                    {Object.keys(recommendedAmounts).length > 0 ? (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        {Object.entries(recommendedAmounts).map(([category, amount]) => (
                          <Grid item xs={12} sm={6} md={4} key={category}>
                            <Card sx={{ bgcolor: '#f5f5f5' }}>
                              <CardContent>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                  {category}
                                </Typography>
                                <Typography variant="h6" color="primary">
                                  ${amount.toFixed(2)}/month
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Alert severity="info">
                        Click "Next" to calculate recommendations
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: '600px',
          borderRadius: 3
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" sx={{ fontWeight: 'bold', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Guided Budget Creation
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: '#667eea' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <AnimatedProgressBar />
        
        <Box sx={{ minHeight: '400px', py: 2 }}>
          {renderStepContent()}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
          startIcon={<ArrowBackIcon />}
          sx={{ color: '#667eea' }}
        >
          Back
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep === STEPS.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleCreateBudget}
            disabled={loading}
            startIcon={<CheckCircleIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              }
            }}
          >
            Create Budget
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowForwardIcon />}
            disabled={loading || analyzing}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              }
            }}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GuidedBudgetModal;
